import {useEffect, useMemo, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {KairosMark} from '@/components/kairos-mark'
import {PorQue} from '@/components/ui/por-que'
import {ListEmptyState} from '@/components/section-card'
import {sgsstPageGuard} from '@/components/no-access-state'
import {PlanLockedPage} from '@/components/plan-upsell'
import {MicroLabel} from '@/components/period-selector'
import {PersonPicker, type Person} from '@/components/person-picker'
import {useResponsablesRoster} from '@/hooks/empresa/identidad/use-employees'
import {useConfirmarPropuesta, usePlanPropuesta, type FilaPropuesta} from '@/hooks/sgsst/plan/use-plan-propuesta'
import {useSgsstPage} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {localToday} from '@/lib/risk'
import {translateApiError} from '@/lib/errors'

type RowState = {
  fila: FilaPropuesta
  checked: boolean
  person: Person | null
  fecha: string
}

const GRUPO_ORDER = ['matriz', 'matriz_legal', 'renovacion', 'cobertura', 'estandar', 'perfil', 'arl'] as const

function PropuestaRow({
  row,
  businessId,
  roster,
  onChange,
}: {
  row: RowState
  businessId: string
  roster: Parameters<typeof PersonPicker>[0]['roster']
  onChange: (next: Partial<RowState>) => void
}) {
  const {t} = useTranslation()
  const {fila} = row
  return (
    <div
      className="-mx-4 border-t border-border px-4 py-3 transition-colors first:border-t-0 hover:bg-secondary/30"
      data-testid="propuesta-fila">
      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={row.checked}
          onChange={(e) => onChange({checked: e.target.checked})}
          data-testid="propuesta-check"
          className="mt-1 accent-primary"
        />
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {fila.standard_code && (
            <span className="font-mono text-xs font-semibold text-primary">{fila.standard_code}</span>
          )}
          {fila.jerarquia && <Tag tone="neutral">{t(`ipevr.jerarquia.${fila.jerarquia}`)}</Tag>}
          <span className="text-sm text-foreground">{fila.titulo}</span>
        </span>
      </label>
      <section
        className="mt-2 ml-7 space-y-1 rounded-lg border border-border bg-secondary/40 p-3"
        data-testid="propuesta-procedencia">
        <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('plan.propuesta.procedencia')}
        </p>
        <p className="text-sm text-foreground">{fila.porque}</p>
        <p className="font-mono text-[11px] text-muted-foreground">{fila.cita}</p>
      </section>
      {row.checked && (
        <div className="mt-2.5 ml-7 grid gap-3 sm:grid-cols-2">
          <div>
            <MicroLabel>{t('plan.propuesta.responsable')}</MicroLabel>
            <div className="mt-1">
              <PersonPicker
                businessId={businessId}
                roster={roster}
                value={row.person}
                onChange={(person) => onChange({person})}
              />
            </div>
          </div>
          <div>
            <MicroLabel>{t('plan.propuesta.fecha')}</MicroLabel>
            <Input
              type="date"
              value={row.fecha}
              onChange={(e) => onChange({fecha: e.target.value})}
              data-testid="propuesta-fecha"
              className="mt-1 font-mono"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export function SgsstPlanPropuestaPage() {
  const {t} = useTranslation()
  const {businessId, business, capabilities, canOperate, state, godReadOnly, loading} = useSgsstPage()
  const navigate = useNavigate()
  const assessment = state?.assessment
  const fase2 = !!assessment?.autoevaluacion_completed_at
  const editable = !!assessment && !assessment.is_closed && !godReadOnly && canOperate
  const ready = editable && fase2
  const {data: propuesta, isLoading: propuestaLoading} = usePlanPropuesta(ready ? businessId : undefined)
  // Excepción: el lado externo del roster va eager porque cada fila se prellena con el responsable
  // por defecto vía `roster.find`, y sin él quedaría «sin asignar».
  const {data: roster} = useResponsablesRoster(canOperate ? businessId : undefined, true)
  const confirmar = useConfirmarPropuesta(businessId!)
  const [rows, setRows] = useState<RowState[] | null>(null)
  const [bulkPerson, setBulkPerson] = useState<Person | null>(null)
  const [bulkFecha, setBulkFecha] = useState('')
  const [pageError, setPageError] = useState<string | null>(null)

  useEffect(() => {
    if (rows !== null || !propuesta || !roster) return
    setRows(
      propuesta.filas.map((fila) => {
        const employee = roster.find((e) => e.id === fila.responsable_default_id)
        const person: Person | null = employee ? {...employee} : null
        return {fila, checked: true, person, fecha: fila.fecha_default}
      })
    )
  }, [rows, propuesta, roster])

  const grupos = useMemo(() => {
    if (!rows) return []
    return GRUPO_ORDER.map((origen) => ({
      origen,
      rows: rows.filter((row) => row.fila.origen === origen),
    })).filter((grupo) => grupo.rows.length > 0)
  }, [rows])

  const planHref = `/businesses/${businessId}/sgsst/plan`
  const guard = sgsstPageGuard(loading, !!business && canOperate, state)
  if (guard !== null || !state) return guard

  if (!capabilities.can_execute_plan) {
    return <PlanLockedPage feature={t('plans.feature.plan')} businessId={businessId} businessName={business?.name} />
  }

  const checkedRows = rows?.filter((row) => row.checked) ?? []
  const incomplete = checkedRows.some((row) => !row.person || !row.fecha)

  function updateRow(fila: FilaPropuesta, next: Partial<RowState>) {
    setRows((current) => current!.map((row) => (row.fila === fila ? {...row, ...next} : row)))
  }

  function applyBulkPerson(person: Person | null) {
    setBulkPerson(person)
    if (person) setRows((current) => current!.map((row) => ({...row, person})))
  }

  function applyBulkFecha(fecha: string) {
    setBulkFecha(fecha)
    if (fecha) setRows((current) => current!.map((row) => ({...row, fecha})))
  }

  function submit() {
    setPageError(null)
    confirmar.mutate(
      checkedRows.map((row) => ({
        origen: row.fila.origen,
        titulo: row.fila.titulo,
        standard_code: row.fila.standard_code,
        riesgo_fila_id: row.fila.riesgo_fila_id,
        legal_item_id: row.fila.legal_item_id,
        jerarquia: row.fila.jerarquia,
        responsible_employee_id: row.person!.id,
        target_date: row.fecha,
      })),
      {
        onSuccess: (result) => navigate(planHref, {state: {created: result.created, skipped: result.skipped}}),
        onError: (err) => setPageError(translateApiError(err)),
      }
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-foreground">
            <KairosMark />
            <h1 className="font-heading text-xl">{t('plan.propuesta.title', {year: assessment?.period_year ?? ''})}</h1>
          </div>
          <p className="mt-1 flex max-w-2xl flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <PorQue
              label={t('plan.propuesta.porque')}
              testId="propuesta-porque"
              contenidoTestId="propuesta-porque-contenido">
              {t('plan.propuesta.porqueCuerpo')}
            </PorQue>
          </p>
        </div>
        <Link to={planHref} className="text-sm font-medium text-primary hover:underline">
          {t('plan.propuesta.backToPlan')}
        </Link>
      </div>

      {pageError && <p className="text-sm text-destructive">{pageError}</p>}

      {!ready ? (
        <div className="surface mx-auto max-w-2xl">
          <ListEmptyState
            page
            title={t('plan.propuesta.notReady')}
            action={
              <Button asChild size="sm">
                <Link to={`/businesses/${businessId}/sgsst`}>{t('plan.propuesta.goToAssessment')}</Link>
              </Button>
            }
          />
        </div>
      ) : propuestaLoading || rows === null ? (
        <Skeleton className="h-48 w-full" />
      ) : rows.length === 0 ? (
        <div className="surface mx-auto max-w-2xl">
          <ListEmptyState
            page
            title={t('plan.propuesta.empty')}
            action={
              <Button asChild size="sm" variant="outline">
                <Link to={planHref}>{t('plan.propuesta.backToPlan')}</Link>
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <div className="surface" data-testid="propuesta-bulk">
            <div className="surface-divider border-b px-4 py-3">
              <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                {t('plan.propuesta.bulkTitle')}
              </p>
            </div>
            <div className="grid gap-3 px-4 py-3.5 sm:grid-cols-2">
              <div>
                <MicroLabel>{t('plan.propuesta.bulkResponsable')}</MicroLabel>
                <div className="mt-1" data-testid="propuesta-bulk-responsable">
                  <PersonPicker
                    businessId={businessId!}
                    roster={roster ?? []}
                    value={bulkPerson}
                    onChange={applyBulkPerson}
                  />
                </div>
              </div>
              <div>
                <MicroLabel>{t('plan.propuesta.bulkFecha')}</MicroLabel>
                <Input
                  type="date"
                  value={bulkFecha}
                  onChange={(e) => applyBulkFecha(e.target.value)}
                  min={localToday()}
                  data-testid="propuesta-bulk-fecha"
                  className="mt-1 font-mono"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3.5">
            {grupos.map((grupo) => (
              <section key={grupo.origen} className="surface overflow-hidden" data-testid="propuesta-grupo">
                <div className="surface-divider flex flex-wrap items-center gap-2.5 border-b px-4 py-3">
                  <span className="font-heading text-sm text-foreground">
                    {t(`plan.propuesta.grupo.${grupo.origen}`)}
                  </span>
                  <span
                    className="rounded-full bg-secondary px-2 py-0.5 font-mono text-[11px] text-muted-foreground"
                    data-testid="grupo-count">
                    {grupo.rows.length}
                  </span>
                  <span className="flex-1" />
                  <span className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
                    {t('plan.propuesta.grupoSeleccionadas', {
                      checked: grupo.rows.filter((r) => r.checked).length,
                      total: grupo.rows.length,
                    })}
                  </span>
                </div>
                <div className="px-4 pb-1.5">
                  {grupo.rows.map((row) => (
                    <PropuestaRow
                      key={`${row.fila.origen}-${row.fila.prioridad}`}
                      row={row}
                      businessId={businessId!}
                      roster={roster ?? []}
                      onChange={(next) => updateRow(row.fila, next)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div
            className="surface sticky bottom-3 flex flex-wrap items-center justify-between gap-3 px-4 py-3 shadow-[var(--shadow-pop)]"
            data-testid="propuesta-footer">
            <p className="text-sm text-muted-foreground">
              {t('plan.propuesta.seleccionadas', {checked: checkedRows.length, total: rows.length})}
              {incomplete && <span className="ml-2 text-destructive">{t('plan.propuesta.faltanDatos')}</span>}
            </p>
            <Button
              disabled={checkedRows.length === 0 || incomplete || confirmar.isPending}
              onClick={submit}
              data-testid="propuesta-crear">
              {t('plan.propuesta.crear', {count: checkedRows.length})}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
